<template>
  <div class="space-y-2 rounded-md border border-input/70 p-3">
    <p class="text-sm font-medium">
      Imported documents
    </p>
    <p v-if="importedWordDocuments.length === 0" class="text-xs text-muted-foreground">
      No imported Word documents yet.
    </p>
    <ul v-else class="space-y-1">
      <li
        v-for="uploadedDocument in importedWordDocuments"
        :key="uploadedDocument.id"
        class="flex flex-wrap items-center justify-between gap-2 rounded-md border border-input/60 px-2 py-1.5"
      >
        <p class="text-sm">
          {{ uploadedDocument.filename }}
        </p>
        <div class="flex items-center gap-2">
          <span class="text-xs text-muted-foreground">{{ formatUploadTimestamp(uploadedDocument.created_at) }}</span>
          <a
            v-if="resolveUploadUrl(uploadedDocument)"
            :href="resolveUploadUrl(uploadedDocument)"
            target="_blank"
            rel="noopener noreferrer"
            class="text-xs font-medium text-primary underline underline-offset-2"
          >
            Open
          </a>
          <button
            type="button"
            :disabled="isReimportDisabled(uploadedDocument)"
            class="text-xs font-medium text-primary underline underline-offset-2 disabled:cursor-not-allowed disabled:opacity-60"
            @click="reimportDocument(uploadedDocument)"
          >
            {{ isReimporting(uploadedDocument) ? 'Re-importing...' : 'Re-import' }}
          </button>
        </div>
      </li>
    </ul>
  </div>
</template>

<script setup>
import { computed, ref } from 'vue'
import { toast } from 'vue-sonner'
import { useProcedureStore } from '../../stores/procedures.js'

const props = defineProps({
  procedure: {
    type: Object,
    default: null,
  },
})
const emit = defineEmits(['reimported-document'])
const procedureStore = useProcedureStore()
const reimportingUploadId = ref(null)

const procedureId = computed(() => {
  return Number(props.procedure?.id ?? 0)
})
const isDraftProcedure = computed(() => {
  return props.procedure?.status === 'draft'
})

const importedWordDocuments = computed(() => {
  if (!Array.isArray(props.procedure?.uploads)) {
    return []
  }

  return [...props.procedure.uploads]
    .filter((upload) => {
      const extension = String(upload?.extension ?? '').toLowerCase()

      return extension === 'doc' || extension === 'docx'
    })
    .sort((firstUpload, secondUpload) => {
      return Date.parse(String(secondUpload?.created_at ?? '')) - Date.parse(String(firstUpload?.created_at ?? ''))
    })
})

const resolveUploadUrl = (upload) => {
  if (!upload || typeof upload !== 'object') {
    return ''
  }

  const candidateUrls = [
    upload.url_secret,
    upload.url,
  ]

  for (const candidateUrl of candidateUrls) {
    if (typeof candidateUrl === 'string' && candidateUrl.trim() !== '') {
      return candidateUrl
    }
  }

  return ''
}

const formatUploadTimestamp = (timestamp) => {
  const normalizedTimestamp = typeof timestamp === 'string' ? timestamp : ''
  const parsedTimestamp = Date.parse(normalizedTimestamp)

  if (Number.isNaN(parsedTimestamp)) {
    return 'Imported recently'
  }

  return new Date(parsedTimestamp).toLocaleString()
}

const isReimportDisabled = (uploadedDocument) => {
  const normalizedUploadId = Number(uploadedDocument?.id ?? 0)

  return !isDraftProcedure.value
    || !Number.isFinite(procedureId.value)
    || procedureId.value <= 0
    || !Number.isFinite(normalizedUploadId)
    || normalizedUploadId <= 0
    || procedureStore.isLoading
    || reimportingUploadId.value !== null
}

const isReimporting = (uploadedDocument) => {
  const normalizedUploadId = Number(uploadedDocument?.id ?? 0)

  return reimportingUploadId.value === normalizedUploadId
}

const reimportDocument = async (uploadedDocument) => {
  if (isReimportDisabled(uploadedDocument)) {
    return
  }

  const normalizedUploadId = Number(uploadedDocument?.id ?? 0)
  const documentName = typeof uploadedDocument?.filename === 'string' && uploadedDocument.filename.trim() !== ''
    ? uploadedDocument.filename
    : 'this document'
  const shouldReimport = confirm(`Re-import ${documentName}? This will replace the current editor content.`)

  if (!shouldReimport) {
    return
  }

  reimportingUploadId.value = normalizedUploadId

  try {
    const importResult = await procedureStore.importProcedureFromWordDocument(procedureId.value, null, {
      uploadId: normalizedUploadId,
      trackLoading: false,
    })
    const importedDocument = importResult?.document

    if (!importedDocument || typeof importedDocument !== 'object' || Array.isArray(importedDocument)) {
      toast.error('Re-import failed.')
      return
    }

    emit('reimported-document', importedDocument)
    toast.success('Word document re-imported.')
  } catch (error) {
    toast.error('Unable to re-import the selected Word document.')
    console.error('Word document re-import failed:', error)
  } finally {
    reimportingUploadId.value = null
  }
}
</script>
